import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { AppInfo } from "@/hooks/use-app-info";
import { formatDateMDY } from "@/lib/format";

interface Props {
  /** The running app's info; only rendered once a version is known. */
  appInfo: AppInfo & { version: string };
}

/**
 * The status bar's version number, as a button that opens what changed in that release
 * (src/release-notes.json, written by the staging release step). A version with no
 * summary says so plainly.
 */
export function ReleaseSummaryDialog({ appInfo }: Props) {
  const { version, release } = appInfo;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`What's new in v${version}`}
          title="What's new in this version"
          className="rounded-sm underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          v{version}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>What's new in v{version}</DialogTitle>
          <DialogDescription>
            {release
              ? `Released ${formatDateMDY(release.date)}`
              : "There's no release summary for this version yet."}
          </DialogDescription>
        </DialogHeader>
        {release && (
          <ul className="list-disc space-y-2 pl-5 text-sm">
            {release.changes.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
